import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { Check } from '@nosinovacao/fe-icons';
import { Button, cn, If, Radio, useI18n } from '@nosinovacao/nosid-mfe-common';
import { Link } from '@tanstack/react-router';
import { ALL_FILTER, type AccountActivityFilterProps } from '../-model/props';

export function AccountActivityFiltersLight({
  availableFilters,
  activeFilters,
  normalizedActiveDevices,
  applicationNames,
  isLoading,
  onChangeFilter,
}: Readonly<AccountActivityFilterProps>) {
  const { t } = useI18n();

  return (
    <div className="grid gap-6 overflow-y-auto">
      <FilterOptionSection
        onClick={(v) => {
          onChangeFilter(activeFilters, 'Orders', v);
        }}
        propertytranslationKey="Order"
        active={activeFilters.Orders}
        available={availableFilters.Orders}
        isLoading={isLoading}
        count={2}
        singleSelect={true}
      />
      <FilterOptionSection
        onClick={(v) => {
          onChangeFilter(activeFilters, 'Activities', v);
        }}
        propertytranslationKey="Activity"
        active={activeFilters.Activities}
        available={availableFilters.Activities}
        transformValue={(v) => t(`AccountActivity.Type.${v}`)}
        isLoading={isLoading}
      />
      <FilterOptionSection
        onClick={(v) => {
          onChangeFilter(activeFilters, 'Devices', v);
        }}
        propertytranslationKey="Devices"
        active={normalizedActiveDevices}
        available={availableFilters.Devices}
        isLoading={isLoading}
        transformValue={(v) => v}
      />
      <FilterOptionSection
        onClick={(v) => {
          onChangeFilter(activeFilters, 'Applications', v);
        }}
        propertytranslationKey="Applications"
        active={activeFilters.Applications}
        available={availableFilters.Applications}
        isLoading={isLoading}
        transformValue={(v) => applicationNames[v] || v}
      />
      <div className="sticky bottom-0 grid grid-cols-2 gap-4 bg-nos-neutral-0/70 max-tablet:pb-8">
        <Button
          data-tid="button-clear-filters"
          skin="light"
          variant="secondary"
          asChild
          className="bg-nos-neutral-0"
        >
          <Link
            to="/security/account-activity"
            search={{ activities: [], orders: [], devices: [], applications: [] }}
          >
            {t('AccountActivity.Filters.ClearAll')}
          </Link>
        </Button>
        <Button data-tid="button-apply-filters" skin="light" asChild>
          <Link
            to="/security/account-activity"
            search={{
              activities: activeFilters.Activities,
              orders: activeFilters.Orders,
              devices: activeFilters.Devices,
              applications: activeFilters.Applications,
            }}
          >
            {t('AccountActivity.Filters.ApplyFilters')}
          </Link>
        </Button>
      </div>
    </div>
  );
}

function FilterOptionSection({
  propertytranslationKey,
  isLoading,
  count = 4,
  active,
  available,
  onClick,
  transformValue,
  singleSelect = false,
}: Readonly<{
  propertytranslationKey: string;
  active: string[];
  available: string[];
  onClick: (newValue: string) => void;
  transformValue?: (value: string) => string;
  isLoading: boolean;
  count?: number;
  singleSelect?: boolean;
}>) {
  const { t } = useI18n();
  const hasAllValue = available.includes(ALL_FILTER);
  function getLabelText(value: string) {
    if (transformValue && value !== ALL_FILTER) {
      return transformValue(value);
    }
    return t(`AccountActivity.Filters.${propertytranslationKey}.Values.${value}`);
  }
  return (
    <div className="grid gap-2 p-1">
      <span data-tid={`title-filter-${propertytranslationKey.toLowerCase()}`}>
        {t(`AccountActivity.Filters.${propertytranslationKey}.Title`)}
      </span>
      <SkeletonLight loading={isLoading} count={count} className="h-12 w-full rounded">
        <div className="grid w-full gap-2">
          <If condition={hasAllValue}>
            <FilterOption
              label={getLabelText(ALL_FILTER)}
              selected={active.includes(ALL_FILTER)}
              onClick={() => {
                onClick(ALL_FILTER);
              }}
              roundedIcon={true}
              singleSelect={singleSelect}
            />
          </If>
          {available
            .filter((option) => option !== ALL_FILTER)
            .map((option) => (
              <FilterOption
                key={option}
                label={getLabelText(option)}
                selected={active.includes(option)}
                onClick={() => {
                  onClick(option);
                }}
                roundedIcon={singleSelect}
                singleSelect={singleSelect}
              />
            ))}
        </div>
      </SkeletonLight>
    </div>
  );
}

function FilterOption({
  label,
  onClick,
  roundedIcon,
  selected,
  singleSelect,
}: Readonly<{
  selected: boolean;
  roundedIcon: boolean;
  label: string;
  onClick: () => void;
  singleSelect: boolean;
}>) {
  const rowClassName =
    'flex w-full min-w-0 items-center gap-3 rounded border border-nos-neutral-200 bg-nos-neutral-0 px-4 py-3 text-nos-neutral-900 font-text-p1 data-[checked=true]:border-2 data-[checked=true]:border-nos-green';

  if (singleSelect) {
    return (
      <label data-checked={selected} className={cn(rowClassName, 'cursor-pointer')}>
        <input
          type="radio"
          checked={selected}
          onChange={onClick}
          className="sr-only"
          aria-label={label}
        />
        <Radio
          skin="light"
          checked={selected}
          onChange={() => {}}
          tabIndex={-1}
          className="pointer-events-none shrink-0"
        />
        <span className="min-w-0 break-all">{label}</span>
      </label>
    );
  }

  return (
    <button
      onClick={onClick}
      type="button"
      aria-pressed={selected}
      data-checked={selected}
      className={rowClassName}
    >
      <If condition={roundedIcon}>
        <RoundedIndicator selected={selected} />
      </If>

      <If condition={!roundedIcon}>
        <SquareIndicator selected={selected} />
      </If>
      <span className="min-w-0 break-all text-left">{label}</span>
    </button>
  );
}

function RoundedIndicator({ selected }: Readonly<{ selected: boolean }>) {
  return (
    <div
      className={cn(
        'relative isolate size-6 shrink-0 rounded-full border border-nos-neutral-900',
        selected &&
          'after:absolute after:left-1/2 after:top-1/2 after:m-auto after:size-[15px] after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full after:bg-nos-neutral-900',
      )}
    />
  );
}

function SquareIndicator({ selected }: Readonly<{ selected: boolean }>) {
  return (
    <div
      className={cn(
        'grid size-6 shrink-0 place-items-center rounded border border-nos-neutral-900',
        selected && 'bg-nos-neutral-900',
      )}
    >
      <If condition={selected}>
        <Check width={20} height={20} className="text-nos-neutral-0" />
      </If>
    </div>
  );
}
